import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import FocusShield from '../components/FocusShield'
import { useKeyboardInset } from '../hooks/useKeyboardInset'
import { useNoDocScrollPush } from '../hooks/useNoDocScrollPush'
import { toastUtil } from '../utils/toast'
import {
  inquiryApi,
  INQUIRY_STATUS_LABEL,
  type InquiryDetailResponse,
} from '../api/inquiry'

const MAX_BODY = 5000

/**
 * 상태 태그 — 목록(SupportList)과 같은 모양.
 * SmallBadgeButton 의 inverse 변형과 같은 값이고, 세 상태가 전부 같은 색이다.
 */
const StatusTag: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex-shrink-0 px-16px py-8px rounded-8px bg-472410-10% font-pretendard font-700 text-14px leading-none text-center text-brown-80%-main">
    {label}
  </span>
)

/** "2026-10-24 14:02" → "10월 24일 14:02" */
const toStamp = (raw: string): string => {
  const m = /^\d{4}-(\d{2})-(\d{2}) (\d{2}:\d{2})/.exec(raw ?? '')
  if (!m) return raw ?? ''
  return `${Number(m[1])}월 ${Number(m[2])}일 ${m[3]}`
}
const toDateLabel = (raw: string): string => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(raw ?? '')
  if (!m) return ''
  return `${Number(m[1])}월 ${Number(m[2])}일`
}

/**
 * 문의 내역 — 대화.
 *
 * 말풍선을 좌우로 나누지 않는다. 카톡처럼 보이면 즉답을 기대하게 되는데,
 * 문의는 하루 이틀 걸리는 통로다. 배경색과 머리줄로만 구분한다.
 *
 * 종료된 문의도 입력란을 막지 않는다. 쓰면 서버가 '답변 대기'로 되돌린다.
 */
const SupportThread: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const inquiryId = Number(id)
  const keyboard = useKeyboardInset()
  useNoDocScrollPush()

  const [data, setData] = useState<InquiryDetailResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [text, setText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const isSendingRef = useRef(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const load = async (scrollToEnd: boolean) => {
    try {
      const res = await inquiryApi.getInquiry(inquiryId)
      setData(res)
      setHasError(false)
      if (scrollToEnd) {
        // 쪽지를 그린 뒤에 내려야 한다
        requestAnimationFrame(() =>
          bottomRef.current?.scrollIntoView({ block: 'end' })
        )
      }
    } catch {
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!Number.isFinite(inquiryId)) {
      setHasError(true)
      setIsLoading(false)
      return
    }
    load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId])

  const handleSend = async () => {
    const body = text.trim()
    if (body.length === 0) {
      toastUtil.error('내용을 입력해 주세요')
      return
    }
    if (isSendingRef.current) return
    isSendingRef.current = true
    setIsSending(true)
    try {
      await inquiryApi.addMessage(inquiryId, body)
      setText('')
      await load(true)
    } catch {
      // 서버 문장은 apiClient 인터셉터가 띄운다
    } finally {
      isSendingRef.current = false
      setIsSending(false)
    }
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <LoadingSpinner size={28} borderWidth={4} />
        </div>
      )
    }
    if (hasError || !data) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <span className="text-style-body1 text-textColorText3">
            문의를 불러오지 못했어요.
          </span>
        </div>
      )
    }

    return (
      <>
        <div className="px-20px pt-18px pb-16px flex items-center gap-10px">
          <div className="flex-1 min-w-0 flex flex-col gap-5px">
            <span className="font-pretendard font-bold text-17px leading-24px text-brown-80%-main">
              {data.inquiry.subject}
            </span>
            <span className="font-pretendard font-regular text-13px leading-18px text-textColorText3">
              {data.inquiry.category_label} · {toDateLabel(data.inquiry.created_at)}
            </span>
          </div>
          <StatusTag label={INQUIRY_STATUS_LABEL[data.inquiry.status]} />
        </div>
        <div className="w-full h-1px bg-brandPrimary-20%" />

        <div className="px-20px pt-18px pb-8px flex flex-col gap-14px">
          {data.messages.map((m) => {
            const isAdmin = m.sender === 'admin'
            return (
              <div
                key={m.id}
                className={`w-full rounded-16px border px-16px py-16px flex flex-col gap-9px ${
                  isAdmin
                    ? 'bg-white border-divider-divider-1'
                    : 'bg-[#f3efe8] border-divider-divider-1'
                }`}
              >
                <div className="flex items-center gap-8px">
                  <span className="font-pretendard font-bold text-14px leading-20px text-brown-80%-main">
                    {m.author}
                  </span>
                  <span className="flex-1 text-right font-pretendard font-regular text-12px leading-16px text-textColorText3">
                    {toStamp(m.created_at)}
                  </span>
                </div>
                <p className="font-pretendard font-regular text-14px leading-170% text-textColorText2 whitespace-pre-wrap break-words">
                  {m.body}
                </p>
                {/* 첨부 사진 — 누르면 원본을 새 창(앱에선 웹뷰)으로 */}
                {m.photos && m.photos.length > 0 && (
                  <div className="mt-10px flex gap-8px overflow-x-auto scrollbar-hide">
                    {m.photos.map((src) => (
                      <a key={src} href={src} target="_blank" rel="noreferrer" className="flex-shrink-0">
                        <img src={src} alt="첨부한 사진" className="w-92px h-116px rounded-12px object-cover bg-472410-5%" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
          <div ref={bottomRef} />
        </div>
      </>
    )
  }

  return (
    /*
     * 키보드가 올라와도 보던 화면은 그대로 있어야 한다.
     *
     * 자세히보기의 댓글창과 같은 방식이다 — 문서는 밀지 않고(useNoDocScrollPush),
     * 키보드가 가린 만큼만 아래를 비운다(useKeyboardInset). 그러면 쪽지 목록은
     * 제자리에 있고 입력 바만 키보드 위로 올라온다.
     */
    <div
      className="w-full h-full flex flex-col bg-backgroundColorBg2"
      style={{
        /*
         * 일부러 transition 을 걸지 않는다.
         *
         * 키보드와 같은 시간 동안 부드럽게 움직이게 해봤는데, 아이폰에서는
         * 그게 '한 번 아래로 갔다 올라오는' 모션으로 보였다. WKWebView 는
         * 커서가 가는 순간 안쪽 스크롤 칸을 즉시 움직여 입력칸을 보여주는데,
         * 우리 여백은 그 뒤에 천천히 차오른다. 칸의 스크롤 가능한 높이가
         * 도중에 계속 바뀌니 브라우저가 스크롤 위치를 다시 잡고, 그 두 단계가
         * 눈에는 왕복으로 보인다.
         *
         * 즉시 적용하면 웹킷이 움직이기 전에 자리가 이미 정해져 있어
         * 한 번에 끝난다. 키보드가 그 위를 덮으며 올라올 뿐이다.
         */
        paddingBottom: keyboard.padding,
      }}
    >
      <div
        className={`app-container flex-1 flex flex-col overflow-hidden ${
          keyboard.padding > 0 ? 'pb-0' : 'pb-20px'
        }`}
      >
        <AppBar title="문의 내역" isBottomBorder />

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">{renderBody()}</div>

        {/* 입력 바 — CommentInputSection 과 같은 모양 */}
        <div className="w-full flex-shrink-0 bg-ivoryKey px-16px pt-6px pb-6px">
          <div className="relative w-full flex items-center min-h-44px py-7px px-12px bg-white rounded-50px border border-solid border-472410-10%">
            <textarea
              id="inquiryReply"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder="댓글을 입력하세요"
              maxLength={MAX_BODY}
              disabled={isSending}
              rows={1}
              className="
                flex-1 min-h-20px max-h-120px py-0 pr-8px
                bg-transparent border-none
                font-pretendard font-regular text-16px leading-180% text-brown-80%-main
                placeholder:text-brown-80%-main
                focus:outline-none resize-none overflow-y-auto scrollbar-hide
              "
            />
            {/*
              오른쪽 40px 은 비워 둔다. 전송 버튼까지 덮으면, 글을 써 둔 채
              키보드를 내렸다가 보내려 할 때 버튼이 안 눌린다.
            */}
            <FocusShield
              targetId="inquiryReply"
              active={!(inputFocused && text.length > 0)}
              rightGap={40}
            />

            {/* CommentInputSection 과 같은 버튼. /images/* 는 1년 캐시라 버전을 붙인다 */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!text.trim() || isSending}
              className="flex-shrink-0 w-24px h-24px flex items-center justify-center bg-transparent border-none cursor-pointer"
            >
              <img
                src="/images/chat-send-icon.png?v=2"
                alt="전송"
                className="w-full h-full object-contain"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportThread
